import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Button from '@/components/ui/Button'
import { useAuthStore } from '@/store/auth'
import {
  loadSituations,
  loadPrograms,
  loadActivities,
  type Situation,
  type Program,
  type Activity,
} from '@/services/content'
import Icon from '@/components/ui/Icon'
import styles from './QuestionnaireResults.module.css'

export default function QuestionnaireResults() {
  const navigate = useNavigate()
  const location = useLocation()
  const childName = useAuthStore((s) => s.user?.child_profile?.name) || 'Your child'

  const situationsSelected: string[] = (location.state as { situationsSelected?: string[] })?.situationsSelected ?? []

  const [matchedSituations, setMatchedSituations] = useState<Situation[]>([])
  const [recommendedPrograms, setRecommendedPrograms] = useState<Program[]>([])
  const [sampleActivities, setSampleActivities] = useState<Activity[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    async function load() {
      const [situations, programs, activities] = await Promise.all([
        loadSituations(),
        loadPrograms(),
        loadActivities(),
      ])

      const matched = situations.filter((s) => situationsSelected.includes(s.id))
      setMatchedSituations(matched)

      const matchingPrograms = programs.filter((p) =>
        p.situationIds.some((sid) => situationsSelected.includes(sid))
      )
      setRecommendedPrograms(matchingPrograms.slice(0, 3))

      const matchingActivities = activities.filter(
        (a) =>
          !a.isPremium &&
          a.situationIds.some((sid) => situationsSelected.includes(sid))
      )
      setSampleActivities(matchingActivities.slice(0, 3))

      setLoaded(true)
    }
    load()
  }, [situationsSelected])

  const handleStart = () => {
    navigate('/')
  }

  if (!loaded) return null

  const hasMatches = matchedSituations.length > 0

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          {hasMatches
            ? `${childName}'s profile is ready!`
            : `You're all set!`}
        </h1>
        <p className={styles.subtitle}>
          {hasMatches
            ? `Based on your answers, we've identified ${matchedSituations.length} area${matchedSituations.length > 1 ? 's' : ''} where MelloMap can help.`
            : `${childName} sounds like they're doing great! Explore our full library of activities for fun ideas.`}
        </p>
      </div>

      {hasMatches ? (
        <>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Areas we can help with</h2>
            <div className={styles.situationList}>
              {matchedSituations.map((situation) => (
                <div key={situation.id} className={styles.situationCard}>
                  <span className={styles.situationIcon}>
                    <Icon name={situation.icon} size={28} />
                  </span>
                  <div className={styles.situationInfo}>
                    <div className={styles.situationName}>{situation.title}</div>
                    <div className={styles.situationDesc}>{situation.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {recommendedPrograms.length > 0 && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Recommended programs</h2>
              <div className={styles.programList}>
                {recommendedPrograms.map((program) => (
                  <div key={program.id} className={styles.programCard}>
                    <div className={styles.programName}>{program.title}</div>
                    <div className={styles.programSubtitle}>
                      {program.duration}-day program
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sampleActivities.length > 0 && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Try these free activities</h2>
              <div className={styles.activityList}>
                {sampleActivities.map((activity) => (
                  <div key={activity.id} className={styles.activityCard}>
                    <span className={styles.activityName}>{activity.title}</span>
                    <span className={styles.activityDuration}>
                      {activity.duration} min
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyEmoji}><Icon name="star" size={22} /></div>
          <h2 className={styles.emptyTitle}>Everything looks good!</h2>
          <p className={styles.emptyText}>
            Explore our activity library for fun, developmental activities you can do together every day.
          </p>
        </div>
      )}

      <div className={styles.footer}>
        <Button onClick={handleStart} fullWidth size="lg">
          Start exploring
        </Button>
      </div>
    </div>
  )
}
